import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { useLayerUpdate } from "../../../hooks/layer/useLayerUpdate";
import { useNotification } from "../../../hooks/notifications/useNotification";
import { useGeometrySelectionConditions } from "../../../hooks/geometrySelection/useGeometrySelectionConditions";
import { getTableFilterConditions } from "../../../ducks/table/selectors";

import { LayerInfo } from "../../../interfaces/resourceInfo";
import { FilterConditionOperation } from "../../../components/LayerFilter/types";

interface UseTableFilterApplyResponse {
  filter: VoidFuncAsync;
  loading: boolean;
}

export const useTableFilterApply = (layer: LayerInfo): UseTableFilterApplyResponse => {
  const { t } = useTranslation("layerFilter");
  const tableFilterConditions = useSelector(getTableFilterConditions);
  const geometrySelectionConditions = useGeometrySelectionConditions();
  const [update, { updating, error, updatedLayer }] = useLayerUpdate(layer);

  const filter = useCallback(() => {
    return update({
      condition: [layer.condition, tableFilterConditions?.[layer.name], geometrySelectionConditions?.[layer.name]]
        .filter(Boolean)
        .join(` ${FilterConditionOperation.AND} `),
    });
  }, [layer.condition, layer.name, tableFilterConditions, geometrySelectionConditions, update]);

  useNotification(error, {
    error: true,
    title: t("filterSaveError"),
    description: t("tryLater"),
  });

  useNotification(updatedLayer, {
    success: true,
    title: t("filterSuccessfullySaved"),
  });

  return {
    filter,
    loading: updating,
  };
};
